import { useEffect, useRef, useState, type FormEvent } from 'react'
import { IMAGE_UPLOAD_ACCEPTED_TYPES, validateImageForUpload } from '@webbuilder/media'
import {
  deleteProfessionalImage,
  LIBRARY_VIDEO_MAX_BYTES,
  LIBRARY_VIDEO_TYPES,
  listProfessionalImagesPage,
  updateProfessionalImage,
  uploadProfessionalImage,
  uploadProfessionalVideo,
  type ProfessionalImage,
} from '../../lib/api'
import { DashboardPageHeader } from '../DashboardPageHeader'
import { IconCloudUpload, IconLoader2, IconPencil, IconPhotoPlus, IconPlayerPlayFilled, IconSearch, IconTrash, IconX } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'
import { useSearchPlaceholder } from '../../hooks/useSearchPlaceholder'

const isVideo = (type: string | null | undefined) => Boolean(type?.startsWith('video/'))

function parseHashtags(value: string) {
  return [...new Set(value
    .split(/[\s,;#]+/)
    .map((tag) => tag.trim().toLocaleLowerCase('es').replace(/^#+/, ''))
    .filter(Boolean))]
    .slice(0, 30)
}

export function AdminImages({ token }: { token: string }) {
  const { t } = useI18n()
  const searchPlaceholder = useSearchPlaceholder('adminImages.search')
  const [images, setImages] = useState<ProfessionalImage[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')
  const [files, setFiles] = useState<File[]>([])
  /* Varias de una vez: cuál va subiendo (1 de N). */
  const [batch, setBatch] = useState<{ at: number; total: number } | null>(null)
  const [title, setTitle] = useState('')
  const [hashtags, setHashtags] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [editing, setEditing] = useState<ProfessionalImage | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editHashtags, setEditHashtags] = useState('')
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  async function load(cursor: string | null = null, append = false, search = query.trim()) {
    if (append) setLoadingMore(true)
    else setLoading(true)
    setError('')
    try {
      /* Aquí, todo: fotos y vídeos. El resto de la app pide sólo fotos. */
      const page = await listProfessionalImagesPage(token, { query: search, cursor, limit: 48, media: 'all' })
      setImages((current) => append ? [...current, ...page.data] : page.data)
      setNextCursor(page.meta.nextCursor)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se pudo cargar la biblioteca.')
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => { void load(null, false, '') }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Una imagen: con su título y sus etiquetas. Dos o más: una tras otra, todas
   * con el título «test» y sin etiquetas (se ordenan después, editándolas). Si
   * una falla, las demás siguen y al final se dice cuántas no subieron.
   */
  async function submitUpload(event: FormEvent) {
    event.preventDefault()
    if (!files.length) {
      setError('Selecciona una imagen para subir.')
      return
    }
    const many = files.length > 1
    setUploading(true)
    setError('')
    let failed = 0
    try {
      for (const [index, file] of files.entries()) {
        if (many) setBatch({ at: index + 1, total: files.length })
        setUploadProgress(0)
        try {
          const input = {
            file,
            title: many ? 'test' : title.trim() || file.name.replace(/\.[^.]+$/, ''),
            hashtags: many ? [] : parseHashtags(hashtags),
            onProgress: setUploadProgress,
          }
          const image = isVideo(file.type) ? await uploadProfessionalVideo(token, input) : await uploadProfessionalImage(token, input)
          setImages((current) => [image, ...current])
        } catch (reason) {
          failed += 1
          if (!many) throw reason
        }
      }
      setFiles([])
      setTitle('')
      setHashtags('')
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (failed) setError(t('adminImages.someFailed', { count: failed, total: files.length }))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se pudo subir la imagen.')
    } finally {
      setUploading(false)
      setUploadProgress(0)
      setBatch(null)
    }
  }

  function selectFiles(list: FileList | null) {
    setError('')
    const picked = Array.from(list ?? [])
    if (!picked.length) {
      setFiles([])
      return
    }
    /* Las que no sirven (formato o tamaño) se apartan y se dice por qué; las demás se quedan. */
    const valid: File[] = []
    let firstError = ''
    for (const file of picked) {
      /* Un vídeo: MP4 o WebM, hasta 95 MB. Lo demás, como imagen. */
      const validationError = isVideo(file.type)
        ? !LIBRARY_VIDEO_TYPES.includes(file.type) ? t('adminImages.videoType')
          : file.size > LIBRARY_VIDEO_MAX_BYTES ? t('adminImages.videoTooBig') : null
        : validateImageForUpload(file)
      if (validationError) firstError ||= `${file.name}: ${validationError}`
      else valid.push(file)
    }
    if (firstError) setError(firstError)
    setFiles(valid)
    if (!valid.length && fileInputRef.current) fileInputRef.current.value = ''
  }

  function openEdit(image: ProfessionalImage) {
    setEditing(image)
    setEditTitle(image.title)
    setEditHashtags(image.hashtags.map((tag) => `#${tag}`).join(' '))
  }

  async function saveEdit(event: FormEvent) {
    event.preventDefault()
    if (!editing || !editTitle.trim()) return
    setSaving(true)
    setError('')
    try {
      const updated = await updateProfessionalImage(token, editing.id, {
        title: editTitle.trim(),
        hashtags: parseHashtags(editHashtags),
        altText: editTitle.trim(),
      })
      setImages((current) => current.map((image) => image.id === updated.id ? updated : image))
      setEditing(null)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se pudieron guardar los cambios.')
    } finally {
      setSaving(false)
    }
  }

  async function remove(image: ProfessionalImage) {
    if (!window.confirm(t('adminImages.confirmDelete', { title: image.title }))) return
    setDeletingId(image.id)
    setError('')
    try {
      await deleteProfessionalImage(token, image.id)
      setImages((current) => current.filter((item) => item.id !== image.id))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se pudo eliminar la imagen.')
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <section className="dash-standard-page dash-admin-images">
      <DashboardPageHeader
        title={t('adminImages.title')}
        subtitle={t('adminImages.subtitle')}
      />

      <form className="dash-image-upload-card" onSubmit={(event) => { void submitUpload(event) }}>
        <button className="dash-image-upload-drop" type="button" onClick={() => fileInputRef.current?.click()}>
          <span><IconPhotoPlus size={21} /></span>
          <strong>{files.length > 1 ? t('adminImages.filesPicked', { count: files.length }) : files[0]?.name ?? t('adminImages.pickFile')}</strong>
          <small>{t('adminImages.formats')}</small>
        </button>
        <input
          ref={fileInputRef}
          hidden
          type="file"
          multiple
          accept={[...IMAGE_UPLOAD_ACCEPTED_TYPES, ...LIBRARY_VIDEO_TYPES].join(',')}
          onChange={(event) => selectFiles(event.target.files)}
        />
        {/* Con varias, título y etiquetas no aplican: van como «test», sin etiquetas. */}
        {files.length > 1 ? (
          <p className="dash-field-hint">{t('adminImages.manyHint')}</p>
        ) : (
          <>
            <label className="dash-field">
              <span>{t('adminImages.titleField')}</span>
              <input value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t('adminImages.titlePlaceholder')} maxLength={140} />
            </label>
            <label className="dash-field">
              <span>{t('adminImages.hashtagsField')}</span>
              <input value={hashtags} onChange={(event) => setHashtags(event.target.value)} placeholder={t('adminImages.hashtagsPlaceholder')} />
            </label>
          </>
        )}
        <button className="dash-primary-btn" type="submit" disabled={uploading || !files.length}>
          {uploading ? <IconLoader2 className="dash-spin" size={15} /> : <IconCloudUpload size={15} />}
          {uploading
            ? `${batch ? `${batch.at}/${batch.total} · ` : ''}${uploadProgress > 0 ? t('adminImages.uploadingPercent', { percent: uploadProgress }) : t('adminImages.compressing')}`
            : files.length > 1 ? t('adminImages.uploadMany', { count: files.length }) : t('adminImages.upload')}
        </button>
        {uploading ? (
          <div className="dash-image-upload-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={uploadProgress}>
            <span style={{ width: `${uploadProgress}%` }} />
          </div>
        ) : null}
      </form>

      <div className="dash-admin-images-toolbar">
        <label>
          <IconSearch size={15} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void load(null, false) }}
            placeholder={searchPlaceholder}
          />
          {query ? <button type="button" onClick={() => { setQuery(''); void load(null, false, '') }}><IconX size={14} /></button> : null}
        </label>
        <button className="dash-secondary-btn" type="button" onClick={() => void load(null, false)}>{t('adminImages.searchBtn')}</button>
      </div>

      {error ? <p className="dash-ai-error">{error}</p> : null}
      {loading ? (
        <div className="dash-admin-images-loading"><IconLoader2 className="dash-spin" size={18} /> {t('adminImages.loading')}</div>
      ) : images.length === 0 ? (
        <div className="dash-placeholder">
          <IconPhotoPlus size={34} aria-hidden="true" />
          <strong>{t('adminImages.empty')}</strong>
          <span>{t('adminImages.emptyBody')}</span>
          <button className="dash-primary-btn" type="button" onClick={() => fileInputRef.current?.click()}>
            <IconCloudUpload size={15} /> {t('adminImages.upload')}
          </button>
        </div>
      ) : (
        <div className="dash-admin-images-grid">
          {images.map((image) => (
            <article className="dash-admin-image-card" key={image.id}>
              <div className="dash-admin-image-media">
                {isVideo(image.mimeType) ? (
                  <>
                    {/* Quieto con su portada; al pasar el ratón, se mueve. */}
                    <video
                      src={image.url}
                      poster={image.posterUrl ?? undefined}
                      muted
                      loop
                      playsInline
                      preload="none"
                      onMouseEnter={(event) => { void event.currentTarget.play().catch(() => {}) }}
                      onMouseLeave={(event) => { event.currentTarget.pause() }}
                    />
                    <span className="dash-admin-image-badge"><IconPlayerPlayFilled size={10} /> {t('adminImages.videoBadge')}</span>
                  </>
                ) : <img src={image.url} alt={image.altText || image.title} loading="lazy" />}
              </div>
              <div className="dash-admin-image-body">
                <strong title={image.title}>{image.title}</strong>
                <div>{image.hashtags.slice(0, 5).map((tag) => <span key={tag}>#{tag}</span>)}</div>
                <footer>
                  <button type="button" onClick={() => openEdit(image)}><IconPencil size={13} /> {t('adminImages.edit')}</button>
                  <button className="danger" type="button" disabled={deletingId === image.id} onClick={() => void remove(image)}>
                    {deletingId === image.id ? <IconLoader2 className="dash-spin" size={13} /> : <IconTrash size={13} />} {t('adminImages.delete')}
                  </button>
                </footer>
              </div>
            </article>
          ))}
        </div>
      )}

      {nextCursor ? (
        <button className="dash-secondary-btn dash-admin-images-more" type="button" disabled={loadingMore} onClick={() => void load(nextCursor, true)}>
          {loadingMore ? <IconLoader2 className="dash-spin" size={14} /> : null} {t('adminImages.loadMore')}
        </button>
      ) : null}

      {editing ? (
        <div className="dash-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) setEditing(null) }}>
          <form className="dash-modal dash-image-edit-modal" onSubmit={(event) => { void saveEdit(event) }}>
            <header className="dash-modal-header"><IconPencil className="dash-modal-icon" size={22} /><h2>{t('adminImages.editTitle')}</h2><p>{t('adminImages.editLead')}</p></header>
            <div className="dash-modal-body">
              <label className="dash-field"><span>{t('adminImages.titleField')}</span><input autoFocus value={editTitle} onChange={(event) => setEditTitle(event.target.value)} maxLength={140} required /></label>
              <label className="dash-field"><span>{t('adminImages.hashtags')}</span><input value={editHashtags} onChange={(event) => setEditHashtags(event.target.value)} placeholder={t('adminImages.editHashtagsPlaceholder')} /></label>
            </div>
            <footer><button className="dash-secondary-btn" type="button" disabled={saving} onClick={() => setEditing(null)}>{t('adminImages.cancel')}</button><button className="dash-primary-btn" type="submit" disabled={saving || !editTitle.trim()}>{saving ? <IconLoader2 className="dash-spin" size={14} /> : null} {t('adminImages.save')}</button></footer>
          </form>
        </div>
      ) : null}
    </section>
  )
}
